import React, { useEffect, useMemo, useState } from "react";
import { X, Check, Plus, Loader2, Calendar as CalendarIcon, Mail, Phone, Lock, Briefcase, Calendar, DollarSign, Percent, Trash2, Camera, User, Timer, Eye, EyeOff, MapPin } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Employee } from "../types/employee";
import api from "../services/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Button } from "./ui/Button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import DateCalendar from "./DateCalendar";
import { format, parseISO, isValid } from "date-fns";
import { cn } from "../lib/utils";
import type { EmployeeEditData } from "../types/employeeEditForm";
import { useToast } from "../hooks/useToast";

function serviceLocationLabel(service: Record<string, unknown>): string {
  const loc = service.location;
  if (loc && typeof loc === "object" && loc !== null && "name" in (loc as object)) {
    const n = String((loc as { name?: string }).name ?? "").trim();
    if (n) return n;
  }
  const ln = service.location_name ?? service.branch_name ?? service.branch;
  if (typeof ln === "string" && ln.trim()) return ln.trim();
  return "";
}

type CatalogServiceRow = {
  id: string;
  matchIds: string[];
  serviceName: string;
  locationName: string;
  serviceId: number;
};

interface EmployeeSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  initialData?: Partial<Employee> | null;
  editData?: EmployeeEditData | null;
  loadingEditData?: boolean;
  onSubmit: (data: Partial<Employee>) => void;
  isSaving?: boolean;
}

function serviceIdsEqual(a: string, b: string): boolean {
  return a === b || (Number(a) === Number(b) && Number.isFinite(Number(a)) && Number.isFinite(Number(b)));
}

/** Match list row / detail API shapes: ids, objects with id, or comma-separated strings */
function coerceServiceIdList(raw: unknown): string[] {
  if (raw == null || raw === "") return [];
  const arr = Array.isArray(raw)
    ? raw
    : typeof raw === "string"
      ? raw.split(/[,;]/).map((s) => s.trim()).filter(Boolean)
      : [raw];
  const out: string[] = [];
  for (const item of arr) {
    if (item == null || item === "") continue;
    if (typeof item === "object") {
      const o = item as Record<string, unknown>;
      const pivot = o.pivot && typeof o.pivot === "object" ? (o.pivot as Record<string, unknown>) : null;
      const id =
        o.business_service_id ??
        pivot?.business_service_id ??
        o.id ??
        o.service_id ??
        pivot?.service_id;
      if (id != null && id !== "") out.push(canonicalServiceIdString(id));
      continue;
    }
    const s = canonicalServiceIdString(item);
    if (s) out.push(s);
  }
  const uniq: string[] = [];
  for (const id of out) {
    if (!uniq.some((x) => serviceIdsEqual(x, id))) uniq.push(id);
  }
  return uniq;
}

function canonicalServiceIdString(v: unknown): string {
  if (v == null || v === "") return "";
  const n = Number(v);
  if (Number.isFinite(n)) return String(n);
  return String(v).trim();
}

function mergeServiceIdLists(...lists: string[][]): string[] {
  const out: string[] = [];
  for (const list of lists) {
    for (const id of list) {
      if (!id) continue;
      if (!out.some((x) => serviceIdsEqual(x, id))) out.push(id);
    }
  }
  return out;
}

function pushDistinctServiceId(list: string[], v: unknown) {
  const s = canonicalServiceIdString(v);
  if (!s) return;
  if (!list.some((x) => serviceIdsEqual(x, s))) list.push(s);
}

function buildServiceMatchIds(raw: Record<string, unknown>, canonicalId: string): string[] {
  const ids: string[] = [];
  pushDistinctServiceId(ids, canonicalId);
  pushDistinctServiceId(ids, raw.id);
  pushDistinctServiceId(ids, raw.business_service_id);
  pushDistinctServiceId(ids, raw.service_id);
  const nested = raw.service;
  if (nested && typeof nested === "object") {
    const o = nested as Record<string, unknown>;
    pushDistinctServiceId(ids, o.id);
    pushDistinctServiceId(ids, o.service_id);
  }
  const pivot = raw.pivot && typeof raw.pivot === "object" ? (raw.pivot as Record<string, unknown>) : null;
  if (pivot) {
    pushDistinctServiceId(ids, pivot.business_service_id);
    pushDistinctServiceId(ids, pivot.service_id);
    pushDistinctServiceId(ids, pivot.id);
  }
  return ids;
}

function isServiceRowSelected(assigned: string[], svc: CatalogServiceRow): boolean {
  return assigned.some((id) => svc.matchIds.some((m) => serviceIdsEqual(id, m)));
}

/** API may return locations as numbers, `{ id, name }`, or numeric strings. */
function normalizeLocationIds(list: unknown): number[] {
  if (!Array.isArray(list)) return [];
  const out: number[] = [];
  for (const item of list) {
    if (typeof item === "number" && Number.isFinite(item)) {
      out.push(item);
    } else if (typeof item === "object" && item !== null && "id" in item) {
      const n = Number((item as { id: unknown }).id);
      if (!Number.isNaN(n)) out.push(n);
    } else if (typeof item === "string" && item.trim() !== "") {
      const n = Number(item.trim());
      if (!Number.isNaN(n)) out.push(n);
    }
  }
  const uniq: number[] = [];
  for (const n of out) {
    if (!uniq.includes(n)) uniq.push(n);
  }
  return uniq;
}

function normalizeAssignedServiceIds(
  initialData: Partial<Employee> | null | undefined,
  editData: EmployeeEditData | null | undefined
): string[] {
  if (editData) {
    const fromSelected = coerceServiceIdList(editData.selected_services);
    const fromEmployeeServices = coerceServiceIdList(
      (editData.employee as Record<string, unknown> | undefined)?.services
    );
    const merged = mergeServiceIdLists(fromSelected, fromEmployeeServices);
    if (merged.length > 0) return merged;
  }

  const row = initialData as Record<string, unknown> | undefined;
  if (!row) return [];

  const details = row.employeeDetails ?? row.employee_details;
  const detailsRec = details && typeof details === "object" ? (details as Record<string, unknown>) : null;

  const chunks = [
    coerceServiceIdList(row.services),
    coerceServiceIdList(row.selected_services),
    coerceServiceIdList(row.employee_services),
    coerceServiceIdList(row.assigned_services),
    coerceServiceIdList(row.business_services),
    coerceServiceIdList(row.service_ids),
    coerceServiceIdList(row.service_id),
    coerceServiceIdList(detailsRec?.services),
    coerceServiceIdList(detailsRec?.selected_services),
  ].filter((l) => l.length > 0);

  if (chunks.length === 0) return [];
  return mergeServiceIdLists(...chunks);
}

const EmployeeSidebar: React.FC<EmployeeSidebarProps> = ({
  isOpen,
  onClose,
  initialData,
  editData,
  loadingEditData = false,
  onSubmit,
  isSaving = false,
}) => {
  const toast = useToast();
  const [activeTab, setActiveTab] = useState<"profile" | "services" | "pin">("profile");
  const [pinCode, setPinCode] = useState("");
  const [pinCodeConfirm, setPinCodeConfirm] = useState("");
  const [pinCodeError, setPinCodeError] = useState("");
  const [showPinCode, setShowPinCode] = useState(false);
  const [showPinCodeConfirm, setShowPinCodeConfirm] = useState(false);
  const [hasVisitedPinTab, setHasVisitedPinTab] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [roleId, setRoleId] = useState<number | null>(null);
  const [shiftId, setShiftId] = useState<number | null>(null);
  const [empJoiningDate, setEmpJoiningDate] = useState("");
  const [workType, setWorkType] = useState<"hourly" | "fixed" | "">("");
  const [hourlyRate, setHourlyRate] = useState("");
  const [fixedPrice, setFixedPrice] = useState("");
  const [workingDays, setWorkingDays] = useState("");
  const [commissionPerBooking, setCommissionPerBooking] = useState("");
  const [employeeGroup, setEmployeeGroup] = useState("");
  const [image, setImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>("");
  const [mobile, setMobile] = useState("");
  const [callingCode, setCallingCode] = useState("+44");
  const [password, setPassword] = useState("");
  const [additionalField, setAdditionalField] = useState("");
  const [assignedServices, setAssignedServices] = useState<string[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<number[]>([]);
  const [fetchedRoles, setFetchedRoles] = useState<Array<{ id: number; name: string; display_name: string }>>([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [fetchedServices, setFetchedServices] = useState<any[]>([]);
  const [loadingServices, setLoadingServices] = useState(false);

  // Fetch roles and services when sidebar opens
  useEffect(() => {
    const fetchRolesAndServices = async () => {
      // Fetch roles if sidebar is open and editData doesn't have roles
      if (isOpen) {
        // --- Roles Fetching ---
        if (!editData?.roles) {
          setLoadingRoles(true);
          try {
            const response = await api.post("/admin/employees/get-roles");
            // Handle different possible response structures
            let rolesArray = null;
            if (response.data?.roles && Array.isArray(response.data.roles)) {
              rolesArray = response.data.roles;
            } else if (response.data?.data?.roles && Array.isArray(response.data.data.roles)) {
              rolesArray = response.data.data.roles;
            } else if (Array.isArray(response.data)) {
              rolesArray = response.data;
            } else if (response.data?.data && Array.isArray(response.data.data)) {
              rolesArray = response.data.data;
            }

            if (rolesArray && rolesArray.length > 0) {
              const formattedRoles = rolesArray.map((role: any) => ({
                id: role.id,
                name: role.name || "",
                display_name: role.display_name || role.name || ""
              }));
              setFetchedRoles(formattedRoles);
            }
          } catch (err: any) {
            console.error("Error fetching roles:", err.response?.data || err.message);
          } finally {
            setLoadingRoles(false);
          }
        }

        // --- Services Fetching ---
        if (!editData?.business_services || editData.business_services.length === 0) {
          setLoadingServices(true);
          try {
            const response = await api.get("/admin/business-services");
            const raw = response.data as Record<string, unknown> | unknown[];
            let list: unknown[] = [];
            if (raw && typeof raw === "object" && !Array.isArray(raw)) {
              const r = raw as Record<string, unknown>;
              if (Array.isArray(r.data)) list = r.data as unknown[];
              else if (Array.isArray(r.services)) list = r.services as unknown[];
            } else if (Array.isArray(raw)) {
              list = raw;
            }
            if (list.length > 0) setFetchedServices(list);
          } catch (err: any) {
            console.error("Error fetching services:", err);
          } finally {
            setLoadingServices(false);
          }
        }

      } else {
        // Reset when sidebar closes
        setFetchedRoles([]);
        setFetchedServices([]);
      }
    };

    fetchRolesAndServices();
  }, [isOpen, editData?.roles, editData?.business_services]);

  const availableServices = useMemo((): CatalogServiceRow[] => {
    const toRow = (service: Record<string, unknown>): CatalogServiceRow | null => {
      const idRaw = service.business_service_id ?? service.id ?? service.service_id;
      if (idRaw == null || idRaw === "") return null;
      const numId = Number(idRaw);
      const id = Number.isFinite(numId) ? String(numId) : String(idRaw).trim();
      if (!id) return null;
      const serviceName = String(service.name ?? service.title ?? service.service_name ?? "Service").trim() || "Service";
      const locationName = serviceLocationLabel(service);
      const serviceId = Number.isFinite(numId) ? numId : 0;
      const matchIds = buildServiceMatchIds(service, id);
      return { id, matchIds, serviceName, locationName, serviceId };
    };

    const mapList = (list: unknown[]): CatalogServiceRow[] => {
      const rows: CatalogServiceRow[] = [];
      for (const raw of list) {
        if (!raw || typeof raw !== "object") continue;
        const row = toRow(raw as Record<string, unknown>);
        if (row) rows.push(row);
      }
      return rows;
    };

    if (editData?.business_services && editData.business_services.length > 0) {
      return mapList(editData.business_services as unknown[]);
    }
    if (fetchedServices.length > 0) {
      return mapList(fetchedServices);
    }
    return [];
  }, [editData, fetchedServices]);

  const allServicesSelected = useMemo(() => {
    if (availableServices.length === 0) return false;
    return availableServices.every((s) => isServiceRowSelected(assignedServices, s));
  }, [availableServices, assignedServices]);

  const selectedCatalogRowCount = useMemo(() => {
    if (availableServices.length === 0) return assignedServices.length;
    return availableServices.filter((svc) => isServiceRowSelected(assignedServices, svc)).length;
  }, [availableServices, assignedServices]);

  const toggleSelectAllServices = () => {
    if (allServicesSelected) {
      setAssignedServices([]);
    } else {
      setAssignedServices(availableServices.map((s) => s.id));
    }
  };

  // Get available roles from editData or fetched roles
  const availableRoles = useMemo(() => {
    if (editData?.roles && editData.roles.length > 0) {
      return editData.roles;
    }
    if (fetchedRoles.length > 0) {
      return fetchedRoles;
    }
    return [];
  }, [editData, fetchedRoles]);

  // Get available shifts from editData
  const availableShifts = useMemo(() => {
    if (editData?.shifts && editData.shifts.length > 0) {
      return editData.shifts;
    }
    return [];
  }, [editData]);

  // Get available groups from editData
  const availableGroups = useMemo(() => {
    if (editData?.groups && editData.groups.length > 0) {
      return editData.groups;
    }
    return [];
  }, [editData]);

  useEffect(() => {
    if (isOpen) {
      // Use editData if available (from API), otherwise use initialData
      const employeeData = (editData?.employee ?? initialData) as Partial<Employee> | undefined;

      setName(String(employeeData?.name ?? ""));
      setEmail(String(employeeData?.email ?? ""));
      setRole(String(employeeData?.role ?? ""));

      // Set role_id from editData employee_details or initialData
      if (editData?.employee_details?.role_id != null) {
        const rid = Number(editData.employee_details.role_id);
        setRoleId(Number.isFinite(rid) ? rid : null);
      } else if (initialData?.role_options && Array.isArray(initialData.role_options)) {
        const selectedRole = initialData.role_options.find(r => r.selected);
        if (selectedRole) {
          setRoleId(selectedRole.id);
        }
      }

      // Set shift_id from editData or initialData employeeDetails
      if (editData?.employee_details?.shift_id != null) {
        const sid = Number(editData.employee_details.shift_id);
        setShiftId(Number.isFinite(sid) ? sid : null);
      } else if ((initialData as any)?.employeeDetails?.shift_id) {
        setShiftId((initialData as any).employeeDetails.shift_id);
      } else {
        setShiftId((initialData as any)?.shift_id || null);
      }

      // Set joining date - check employeeDetails.joining_date from list API response
      const joiningDate = editData?.employee_details?.emp_joining_date ||
        (initialData as any)?.employeeDetails?.joining_date ||
        (initialData as any)?.emp_joining_date ||
        (initialData as any)?.joining_date || "";
      setEmpJoiningDate(joiningDate);

      // Set work type - check employeeDetails.work_type from list API response
      const workTypeValue = editData?.employee_details?.work_type ||
        (initialData as any)?.employeeDetails?.work_type ||
        (initialData as any)?.work_type || "";
      setWorkType(workTypeValue === "hourly" || workTypeValue === "fixed" ? workTypeValue : "");

      // Set work type related fields - check employeeDetails.rate from list API response
      // Rate can be hourly_rate or fixed_price depending on work_type
      const employeeDetails = (initialData as any)?.employeeDetails;
      if (employeeDetails?.rate) {
        if (workTypeValue === "hourly") {
          setHourlyRate(employeeDetails.rate);
        } else if (workTypeValue === "fixed") {
          setFixedPrice(employeeDetails.rate);
        }
      } else {
        // Fallback to editData or other sources
        setHourlyRate(editData?.employee_details?.hourly_rate || (initialData as any)?.hourly_rate || "");
        setFixedPrice(editData?.employee_details?.fixed_price || (initialData as any)?.fixed_price || "");
      }

      // Set working days - check employeeDetails.working_days from list API response
      setWorkingDays(
        editData?.employee_details?.working_days ||
        (initialData as any)?.employeeDetails?.working_days ||
        (initialData as any)?.working_days || ""
      );

      // Set commission - check employeeDetails.commission from list API response
      setCommissionPerBooking(
        editData?.employee_details?.commission_per_booking ||
        editData?.employee_details?.commission ||
        (initialData as any)?.employeeDetails?.commission ||
        (initialData as any)?.commission_per_booking || ""
      );

      // Set employee group from editData or initialData
      if (editData?.employee && editData.groups) {
        const gid = (editData.employee as Record<string, unknown>).group_id;
        const group = gid != null ? editData.groups.find((g) => Number(g.id) === Number(gid)) : undefined;
        setEmployeeGroup(group?.name || "");
      } else {
        setEmployeeGroup((initialData as any)?.employee_group || initialData?.group || "");
      }

      setImage(null);
      setImagePreview(
        String((employeeData as { user_image_url?: string })?.user_image_url ?? employeeData?.image ?? "")
      );
      setMobile(String(employeeData?.mobile ?? ""));
      setCallingCode(String(employeeData?.calling_code ?? "+44"));
      setPassword(""); // Password is not pre-filled for security
      setAdditionalField((employeeData as any)?.additional_field || "");

      setAssignedServices(normalizeAssignedServiceIds(initialData, editData ?? null));

      // Set selected locations from editData (always numeric ids for FormData)
      if (editData?.selected_locations && editData.selected_locations.length > 0) {
        setSelectedLocations(normalizeLocationIds(editData.selected_locations));
      } else if ((initialData as any)?.locations && Array.isArray((initialData as any).locations)) {
        setSelectedLocations(normalizeLocationIds((initialData as any).locations));
      } else {
        setSelectedLocations([]);
      }

      setPinCode("");
      setPinCodeConfirm("");
      setPinCodeError("");
      setShowPinCode(false);
      setShowPinCodeConfirm(false);
      setHasVisitedPinTab(false);
      setActiveTab("profile");
    } else {
      setName("");
      setEmail("");
      setRole("");
      setRoleId(null);
      setShiftId(null);
      setEmpJoiningDate("");
      setWorkType("");
      setHourlyRate("");
      setFixedPrice("");
      setWorkingDays("");
      setCommissionPerBooking("");
      setEmployeeGroup("");
      setImage(null);
      setImagePreview("");
      setMobile("");
      setCallingCode("+44");
      setPassword("");
      setAdditionalField("");
      setAssignedServices([]);
      setSelectedLocations([]);
      setPinCode("");
      setPinCodeConfirm("");
      setPinCodeError("");
      setShowPinCode(false);
      setShowPinCodeConfirm(false);
      setHasVisitedPinTab(false);
    }
  }, [isOpen, initialData, editData]);

  const handleSave = () => {
    setPinCodeError("");
    const p1 = pinCode.trim();
    const p2 = pinCodeConfirm.trim();

    if (!initialData?.id && !password.trim()) {
      toast.error("Password is required for new employees");
      setActiveTab("profile");
      return;
    }

    // Only validate PIN when user has visited the PIN tab (avoids autofill/mismatch when not changing PIN)
    if (hasVisitedPinTab) {
      if (p1 || p2) {
        if (p1 !== p2) {
          setPinCodeError("PIN codes do not match");
          setActiveTab("pin");
          return;
        }
        if (!p1) {
          setPinCodeError("Please enter a PIN code");
          setActiveTab("pin");
          return;
        }
      }
    } else {
      // User never opened PIN tab: treat as no change, ignore any autofilled values
      // (We pass undefined for edit / empty for add in the payload below)
    }

    const resolvedLocationIds =
      selectedLocations.length > 0
        ? selectedLocations
        : normalizeLocationIds((initialData as Record<string, unknown> | undefined)?.locations);
    /** Backend requires at least one location; default head office / primary location. */
    const DEFAULT_LOCATION_ID = 1;
    const locationForApi =
      resolvedLocationIds.length > 0 ? resolvedLocationIds : [DEFAULT_LOCATION_ID];

    const rawGroupId =
      (editData?.employee as Record<string, unknown> | undefined)?.group_id ??
      (initialData as Record<string, unknown> | undefined)?.group_id;
    const groupIdForApi =
      rawGroupId != null && rawGroupId !== "" && String(rawGroupId) !== "0" ? String(rawGroupId) : "0";

    const payload: Partial<Employee> & {
      imageFile?: File | null;
      role_id?: number | null;
      shift_id?: number | null;
      emp_joining_date?: string;
      work_type?: string;
      hourly_rate?: string;
      fixed_price?: string;
      working_days?: string;
      commission?: string;
      employee_group?: string;
      password?: string;
      additional_field?: string;
      service_id?: number[];
      location?: number[];
      group_id?: string;
      pin_code?: string;
    } = {
      id: initialData?.id,
      name,
      email,
      mobile: mobile || undefined,
      calling_code: callingCode || undefined,
      password: password || undefined,
      additional_field: additionalField || undefined,
      image: imagePreview || undefined,
      service_id: assignedServices.length > 0 ? assignedServices.map(id => parseInt(id)).filter(id => !isNaN(id)) : undefined, // Convert to array of number IDs
      location: locationForApi,
      group_id: groupIdForApi,
      imageFile: image, // Pass the File object separately
      role_id: roleId,
      shift_id: shiftId,
      emp_joining_date: empJoiningDate || undefined,
      work_type: workType || undefined,
      hourly_rate: hourlyRate || undefined,
      fixed_price: fixedPrice || undefined,
      working_days: workingDays || undefined,
      commission: commissionPerBooking || undefined,
      employee_group: employeeGroup || undefined,
      // Edit: only send PIN when user visited tab and entered one. Add: send "" when empty to prevent backend default 123456.
      pin_code: initialData?.id
        ? (hasVisitedPinTab && p1 ? p1 : undefined)
        : (hasVisitedPinTab ? (p1 || "") : ""),
    };
    onSubmit(payload);
  };

  const handleDateSelect = (date: Date) => {
    setEmpJoiningDate(format(date, "yyyy-MM-dd"));
  };

  const formattedJoiningDate = useMemo(() => {
    if (!empJoiningDate) return "Select date";
    const date = parseISO(empJoiningDate);
    return isValid(date) ? format(date, "PPP") : "Select date";
  }, [empJoiningDate]);

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-[2px] z-[99]"
            onClick={onClose}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="fixed top-0 right-0 h-full w-full sm:w-[500px] md:w-[600px] lg:w-[700px] bg-[#F8F9FD] shadow-2xl z-[1000] flex flex-col overflow-visible"
          >
            {/* Standardized Floating Close Button */}
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={onClose}
              className="absolute lg:-left-12 top-4 lg:top-9 right-4 lg:right-auto w-11 h-11 rounded-full bg-white shadow-md flex items-center justify-center text-[#885ABB] hover:bg-[#402B69] hover:text-white transition-all z-[1100] group"
              aria-label="Close"
            >
              <X size={20} strokeWidth={3} className="group-hover:rotate-90 transition-transform duration-300" />
            </motion.button>
            {/* Header */}
            <div className="bg-white px-6 py-6 border-b border-gray-100 flex items-center justify-between sticky top-0 z-10 shrink-0">
              <div className="flex items-center gap-4">
                <div>
                  <div className="text-[10px] font-bold text-[#885ABB] uppercase tracking-widest mb-0.5">
                    {initialData?.id ? "Edit Employee" : "New Employee"}
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">
                    {initialData?.id ? name || "Employee Details" : "Add Staff Member"}
                  </h2>
                </div>
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="bg-white px-6 pt-2 shrink-0 border-b border-gray-100">
              <div className="flex gap-8">
                {[
                  { id: "profile", label: "Profile Information", icon: User },
                  { id: "services", label: "Service Access", icon: Briefcase },
                  { id: "pin", label: "PIN Code", icon: Lock },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => {
                      setActiveTab(tab.id as any);
                      if (tab.id === "pin") setHasVisitedPinTab(true);
                    }}
                    className={cn(
                      "flex items-center gap-2 py-4 text-sm font-bold transition-all relative",
                      activeTab === tab.id
                        ? "text-[#885ABB]"
                        : "text-gray-400 hover:text-gray-600"
                    )}
                  >
                    <tab.icon size={16} />
                    {tab.label}
                    {activeTab === tab.id && (
                      <motion.div
                        layoutId="activeTabEmployee"
                        className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#885ABB] rounded-full"
                      />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto custom-scrollbar p-6">
              <div className="max-w-2xl mx-auto space-y-6">
                {activeTab === "profile" && (
                  <>
                    {/* Portrait Section */}
                    <div className="bg-white rounded-2xl border border-gray-100 p-8 shadow-sm">
                      <div className="flex flex-col items-center text-center">
                        <div className="relative group">
                          <div className="w-28 h-28 rounded-3xl bg-purple-50 border-2 border-dashed border-purple-200 flex items-center justify-center overflow-hidden transition-all group-hover:border-[#885ABB]">
                            {imagePreview ? (
                              <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                            ) : (
                              <User className="w-12 h-12 text-purple-200" />
                            )}
                            <label className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer text-white">
                              <Camera size={24} />
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => {
                                  const file = e.target.files && e.target.files[0];
                                  if (!file) return;
                                  setImage(file);
                                  const reader = new FileReader();
                                  reader.onload = () => {
                                    const result = typeof reader.result === "string" ? reader.result : "";
                                    setImagePreview(result);
                                  };
                                  reader.readAsDataURL(file);
                                }}
                              />
                            </label>
                          </div>
                          {imagePreview && (
                            <button
                              onClick={() => { setImage(null); setImagePreview(""); }}
                              className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-white shadow-md border border-gray-100 flex items-center justify-center text-red-500 hover:bg-red-50 transition-colors"
                            >
                              <X size={14} strokeWidth={3} />
                            </button>
                          )}
                        </div>
                        <h4 className="mt-4 font-bold text-gray-900">Profile Photo</h4>
                        <p className="text-xs text-gray-400 mt-1 max-w-[200px]">
                          Recommended: Square image, 500x500px in JPG or PNG format.
                        </p>
                      </div>
                    </div>

                    {/* Basic Info */}
                    <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-6 shadow-sm">
                      <div className="flex items-center gap-3 border-b border-gray-50 pb-4">
                        <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center">
                          <User size={18} className="text-[#885ABB]" />
                        </div>
                        <h3 className="font-bold text-gray-900">General Information</h3>
                      </div>

                      <div className="grid grid-cols-1 gap-6">
                        <div className="space-y-2">
                          <label className="text-xs font-bold text-gray-400 uppercase tracking-widest px-1">Full Name</label>
                          <div className="relative">
                            <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300" />
                            <input
                              value={name}
                              onChange={(e) => setName(e.target.value)}
                              placeholder=""
                              className="w-full h-12 pl-12 pr-4 bg-gray-50/50 border border-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#885ABB]/20 focus:border-[#885ABB] transition-all text-sm font-semibold text-gray-700"
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <label className="text-xs font-bold text-gray-400 uppercase tracking-widest px-1">Email Address</label>
                          <div className="relative">
                            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300" />
                            <input
                              type="email"
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              placeholder=""
                              className="w-full h-12 pl-12 pr-4 bg-gray-50/50 border border-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#885ABB]/20 focus:border-[#885ABB] transition-all text-sm font-semibold text-gray-700"
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <label className="text-xs font-bold text-gray-400 uppercase tracking-widest px-1">Mobile Phone</label>
                          <div className="flex gap-2">
                            <Select value={callingCode} onValueChange={setCallingCode}>
                              <SelectTrigger className="w-32 h-12 bg-gray-50/50 border-gray-100 rounded-xl font-bold flex shrink-0">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="z-[1100]">
                                <SelectItem value="+44">🇬🇧 +44</SelectItem>
                                <SelectItem value="+1">🇺🇸 +1</SelectItem>
                                <SelectItem value="+353">🇮🇪 +353</SelectItem>
                                <SelectItem value="+61">🇦🇺 +61</SelectItem>
                                <SelectItem value="+91">🇮🇳 +91</SelectItem>
                                <SelectItem value="+92">🇵🇰 +92</SelectItem>
                              </SelectContent>
                            </Select>
                            <div className="relative flex-1">
                              <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300" />
                              <input
                                value={mobile}
                                onChange={(e) => setMobile(e.target.value)}
                                placeholder=""
                                className="w-full h-12 pl-12 pr-4 bg-gray-50/50 border border-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#885ABB]/20 focus:border-[#885ABB] transition-all text-sm font-semibold text-gray-700"
                              />
                            </div>
                          </div>
                        </div>

                        {!initialData?.id && (
                          <div className="space-y-2">
                            <label className="text-xs font-bold text-gray-400 uppercase tracking-widest px-1">Initial Password</label>
                            <div className="relative">
                              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300" />
                              <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                className="w-full h-12 pl-12 pr-4 bg-gray-50/50 border border-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#885ABB]/20 focus:border-[#885ABB] transition-all text-sm font-semibold text-gray-700"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Employment Details */}
                    <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-6 shadow-sm">
                      <div className="flex items-center gap-3 border-b border-gray-50 pb-4">
                        <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center">
                          <Briefcase size={18} className="text-[#885ABB]" />
                        </div>
                        <h3 className="font-bold text-gray-900">Employment Details</h3>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <label className="text-xs font-bold text-gray-400 uppercase tracking-widest px-1">Role / Job Title</label>
                          <Select
                            value={String(roleId || "")}
                            onValueChange={(val) => {
                              const id = parseInt(val);
                              setRoleId(id);
                              const found = availableRoles.find(r => r.id === id);
                              if (found) setRole(found.display_name || found.name);
                            }}
                          >
                            <SelectTrigger className="h-12 bg-gray-50/50 border-gray-100 rounded-xl font-bold">
                              <SelectValue placeholder="Select role" />
                            </SelectTrigger>
                            <SelectContent className="z-[1100]">
                              {availableRoles.map(r => (
                                <SelectItem key={r.id} value={String(r.id)}>{r.display_name || r.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <label className="text-xs font-bold text-gray-400 uppercase tracking-widest px-1">Joining Date</label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <button className="w-full h-12 px-4 bg-gray-50/50 border border-gray-100 rounded-xl flex items-center gap-3 text-sm font-semibold text-gray-700 hover:bg-gray-100/50 transition-all">
                                <CalendarIcon className="w-4 h-4 text-purple-400" />
                                {formattedJoiningDate}
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0 z-[1100]" align="start">
                              <DateCalendar
                                selectedDate={empJoiningDate ? parseISO(empJoiningDate) : new Date()}
                                onSelectDate={handleDateSelect}
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <label className="text-xs font-bold text-gray-400 uppercase tracking-widest px-1">Compensation Type</label>
                        <div className="flex gap-4">
                          {[
                            { id: "hourly", label: "Hourly Rate", icon: Timer },
                            { id: "fixed", label: "Fixed Salary", icon: DollarSign },
                          ].map((type) => (
                            <button
                              key={type.id}
                              onClick={() => setWorkType(type.id as any)}
                              className={cn(
                                "flex-1 flex items-center justify-center gap-3 py-4 rounded-2xl border transition-all font-bold text-sm",
                                workType === type.id
                                  ? "bg-[#F8F5FF] border-[#885ABB] text-[#885ABB] shadow-sm"
                                  : "bg-white border-gray-100 text-gray-400 hover:border-gray-200"
                              )}
                            >
                              <type.icon size={18} />
                              {type.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {workType === "hourly" && (
                        <div className="space-y-2">
                          <label className="text-xs font-bold text-gray-400 uppercase tracking-widest px-1">Hourly Rate</label>
                          <div className="relative">
                            <input
                              type="number"
                              value={hourlyRate}
                              onChange={(e) => setHourlyRate(e.target.value)}
                              placeholder="0.00"
                              className="w-full h-12 px-4 bg-gray-50/50 border border-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#885ABB]/20 focus:border-[#885ABB] transition-all text-sm font-bold text-gray-700"
                            />
                          </div>
                        </div>
                      )}

                      {workType === "fixed" && (
                        <div className="grid grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <label className="text-xs font-bold text-gray-400 uppercase tracking-widest px-1">Base Salary</label>
                            <div className="relative">
                              <input
                                type="number"
                                value={fixedPrice}
                                onChange={(e) => setFixedPrice(e.target.value)}
                                placeholder="0.00"
                                className="w-full h-12 px-4 bg-gray-50/50 border border-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#885ABB]/20 focus:border-[#885ABB] transition-all text-sm font-bold text-gray-700"
                              />
                            </div>
                          </div>
                          <div className="space-y-2">
                            <label className="text-xs font-bold text-gray-400 uppercase tracking-widest px-1">Work Days / Mo</label>
                            <input
                              type="number"
                              value={workingDays}
                              onChange={(e) => setWorkingDays(e.target.value)}
                              placeholder="20"
                              className="w-full h-12 px-4 bg-gray-50/50 border border-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#885ABB]/20 focus:border-[#885ABB] transition-all text-sm font-bold text-gray-700"
                            />
                          </div>
                        </div>
                      )}

                      <div className="space-y-2">
                        <label className="text-xs font-bold text-gray-400 uppercase tracking-widest px-1">Commission Rate (%)</label>
                        <div className="relative">
                          <Percent className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300" />
                          <input
                            type="number"
                            value={commissionPerBooking}
                            onChange={(e) => setCommissionPerBooking(e.target.value)}
                            placeholder="0.0"
                            className="w-full h-12 px-4 bg-gray-50/50 border border-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#885ABB]/20 focus:border-[#885ABB] transition-all text-sm font-bold text-gray-700"
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {activeTab === "pin" && (
                  <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-6 shadow-sm min-h-[400px]">
                    <div className="flex items-center gap-3 border-b border-gray-50 pb-4">
                      <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center">
                        <Lock size={18} className="text-[#885ABB]" />
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-bold text-gray-900">PIN Code</h3>
                        {(editData?.employee as any)?.pin_protected || (initialData as any)?.pin_protected ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-700 border border-amber-200">
                            PIN protected
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <p className="text-sm text-gray-500">
                      Set a PIN code for this employee to use when logging in via the Employee login drawer.
                    </p>
                    <div className="grid grid-cols-1 gap-6">
                      <div className="space-y-2">
                        <label className="text-xs font-bold text-gray-400 uppercase tracking-widest px-1">PIN Code</label>
                        <div className="relative">
                          <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300" />
                          <input
                            type={showPinCode ? "text" : "password"}
                            inputMode="numeric"
                            pattern="[0-9]*"
                            value={pinCode}
                            onChange={(e) => setPinCode(e.target.value.replace(/\D/g, "").slice(0, 8))}
                            placeholder="Enter PIN (numbers only)"
                            maxLength={8}
                            autoComplete="new-password"
                            className="w-full h-12 pl-12 pr-12 bg-gray-50/50 border border-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#885ABB]/20 focus:border-[#885ABB] transition-all text-sm font-semibold text-gray-700"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPinCode((s) => !s)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#885ABB] transition-colors"
                            aria-label={showPinCode ? "Hide PIN" : "Show PIN"}
                          >
                            {showPinCode ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs font-bold text-gray-400 uppercase tracking-widest px-1">Confirm PIN Code</label>
                        <div className="relative">
                          <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300" />
                          <input
                            type={showPinCodeConfirm ? "text" : "password"}
                            inputMode="numeric"
                            pattern="[0-9]*"
                            value={pinCodeConfirm}
                            onChange={(e) => setPinCodeConfirm(e.target.value.replace(/\D/g, "").slice(0, 8))}
                            placeholder="Confirm PIN"
                            maxLength={8}
                            autoComplete="new-password"
                            className="w-full h-12 pl-12 pr-12 bg-gray-50/50 border border-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#885ABB]/20 focus:border-[#885ABB] transition-all text-sm font-semibold text-gray-700"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPinCodeConfirm((s) => !s)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#885ABB] transition-colors"
                            aria-label={showPinCodeConfirm ? "Hide PIN" : "Show PIN"}
                          >
                            {showPinCodeConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                        </div>
                      </div>
                      {pinCodeError && (
                        <p className="text-sm text-red-500">{pinCodeError}</p>
                      )}
                    </div>
                  </div>
                )}

                {activeTab === "services" && (
                  <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-6 shadow-sm min-h-[400px]">
                    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-50 pb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center">
                          <Briefcase size={18} className="text-[#885ABB]" />
                        </div>
                        <h3 className="font-bold text-gray-900">Service Permissions</h3>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        {availableServices.length > 0 && !loadingServices && !loadingEditData ? (
                          <button
                            type="button"
                            onClick={toggleSelectAllServices}
                            className="text-xs font-bold text-[#885ABB] hover:text-[#6f47a2] underline-offset-2 hover:underline px-2 py-1 rounded-lg hover:bg-purple-50/80 transition-colors"
                          >
                            {allServicesSelected ? "Clear all" : "Select all"}
                          </button>
                        ) : null}
                        <div className="bg-purple-50 px-3 py-1 rounded-full text-[10px] font-bold text-[#885ABB] uppercase tracking-wider">
                          {selectedCatalogRowCount} Selected
                        </div>
                      </div>
                    </div>

                    {loadingServices || loadingEditData ? (
                      <div className="flex flex-col items-center justify-center py-20 gap-4">
                        <Loader2 className="w-8 h-8 animate-spin text-[#885ABB]" />
                        <p className="text-sm font-medium text-gray-400">Loading your services catalogue...</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 gap-3">
                        {availableServices.length > 0 ? (
                          availableServices.map((svc, svcIndex) => {
                            const isSelected = isServiceRowSelected(assignedServices, svc);
                            return (
                              <button
                                key={`${svc.id}-${svcIndex}`}
                                type="button"
                                onClick={() =>
                                  setAssignedServices((prev) =>
                                    isSelected
                                      ? prev.filter(
                                          (id) => !svc.matchIds.some((m) => serviceIdsEqual(id, m))
                                        )
                                      : prev.some((id) => svc.matchIds.some((m) => serviceIdsEqual(id, m)))
                                        ? prev
                                        : [...prev, svc.id]
                                  )
                                }
                                className={cn(
                                  "group flex items-center justify-between p-4 rounded-xl border transition-all duration-200 text-left",
                                  isSelected
                                    ? "bg-[#F8F5FF] border-[#C4A6E1] translate-x-1"
                                    : "bg-white border-gray-100 hover:border-gray-200 hover:bg-gray-50"
                                )}
                              >
                                <div className="flex flex-col">
                                  <span className={cn(
                                    "text-sm font-bold transition-colors",
                                    isSelected ? "text-[#885ABB]" : "text-gray-700"
                                  )}>
                                    {svc.serviceName}
                                  </span>
                                  {/* {svc.locationName ? (
                                    <span className="mt-1 flex items-center gap-1 text-xs font-medium text-gray-500">
                                      <MapPin className="h-3.5 w-3.5 shrink-0 text-gray-400" aria-hidden />
                                      {svc.locationName}
                                    </span>
                                  ) : null} */}
                                  <span className="text-[10px] text-gray-400 font-medium uppercase mt-0.5">
                                    Full access for this service
                                  </span>
                                </div>
                                <div className={cn(
                                  "w-8 h-8 rounded-lg flex items-center justify-center transition-all",
                                  isSelected
                                    ? "bg-[#885ABB] text-white rotate-0"
                                    : "bg-gray-50 text-gray-300 group-hover:text-[#885ABB] -rotate-90"
                                )}>
                                  {isSelected ? <Check size={18} strokeWidth={3} /> : <Plus size={18} />}
                                </div>
                              </button>
                            );
                          })
                        ) : (
                          <div className="text-center py-12">
                            <p className="text-sm text-gray-400">No services found in your business profile.</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="bg-white px-6 py-4 border-t border-gray-100 flex items-center justify-end shrink-0">
              <div className="flex gap-3">
                <Button
                  variant="ghost"
                  onClick={onClose}
                  className="font-bold text-gray-400"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="bg-[#885ABB] hover:bg-[#7a4fa8] text-white px-8 font-bold shadow-lg shadow-purple-200 min-w-[140px]"
                >
                  {isSaving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    "Confirm & Save"
                  )}
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default EmployeeSidebar;
